import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  Paper,
  Typography,
  Container,
  CircularProgress,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Loading } from "../../../components/Loading";
import NavigationHeader from "../../../components/NavigationHeader";
import { getValidToken } from "../../../utils/authUtils";
import { useToast } from "../../../components/Toast";
import AddBranchModal from "./AddBranchModal";
import { tableCellStyles, tableHeaderStyles } from "../../../utils/styles";
import { formatCurrency } from "../../../utils/utils";

export interface Branch {
  id?: string;
  companyId: string;
  name: string;
  contactPerson: string;
  contactNumber: string;
  email: string | null;
  address: string | null;
  commissionType?: string;
  commissionValue?: string;
}

const Branch = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | undefined>();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { showToast } = useToast();

  useEffect(() => {
    loadBranchs();
  }, [page]);

  const loadBranchs = async () => {
    setIsLoading(true);
    try {
      const token = await getValidToken();
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: "10",
      });
      const url = `${process.env.REACT_APP_API_HOST}master-data/branch?${queryParams}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.isSuccess) {
        setBranches(response.data?.data);
        setTotalPages(response.data?.pagination?.pages || 1);
      } else {
        setBranches([]);
      }
    } catch (error) {
      showToast("Error fetching branches data", "error");
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBranch = () => {
    setSelectedBranch(undefined);
    setIsModalOpen(true);
  };

  const handleEditBranch = (branch: Branch) => {
    setSelectedBranch(branch);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBranch(undefined);
  };

  const handleSubmit = async (branchData: any) => {
    setIsSubmitting(true);
    const token = await getValidToken();
    if (selectedBranch) {
      // Update existing branch
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/branch/${branchData?.id}`,
          branchData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Branch updated successfully", "success");
          loadBranchs();
        } else {
          showToast(`Error updating branch`, "error");
        }
      } catch (error) {
        console.error("Error updating branch", error);
        showToast(`Error updating branch`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new branch
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/branch`,
          branchData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Branch added successfully", "success");
          loadBranchs();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error adding branch", error);
        showToast(`Error adding branch`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={false} />
    ) : (
      <Loading message="Loading branches..." fullScreen={false} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Branch" },
        ]}
        title="Branch"
        button={{
          label: "Add Branch",
          onClick: handleAddBranch,
          icon: <AddIcon />,
        }}
      />

      {branches.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No branches found
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="Branches table">
              <TableHead>
                <TableRow>
                  <TableCell {...tableHeaderStyles}>Branch Name</TableCell>
                  <TableCell {...tableHeaderStyles}>Commission</TableCell>
                  <TableCell {...tableHeaderStyles}>Contact Person</TableCell>
                  <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                  <TableCell {...tableHeaderStyles}>Email</TableCell>
                  <TableCell {...tableHeaderStyles}>Address</TableCell>
                  <TableCell {...tableHeaderStyles} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((branch) => (
                  <TableRow
                    key={branch.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <TableCell {...tableCellStyles}>{branch.name}</TableCell>
                    <TableCell {...tableCellStyles}>
                      {branch.commissionType === "Percentage"
                        ? `${branch.commissionValue}%`
                        : branch.commissionType === "Fixed"
                        ? `${formatCurrency(branch.commissionValue)}/item`
                        : "-"}
                    </TableCell>
                    <TableCell>{branch.contactPerson}</TableCell>
                    <TableCell>{branch.contactNumber}</TableCell>
                    <TableCell>{branch.email || "-"}</TableCell>
                    <TableCell>{branch.address || "-"}</TableCell>
                    <TableCell {...tableCellStyles} align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditBranch(branch)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <AddBranchModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        branch={selectedBranch}
        isEdit={!!selectedBranch}
      />
    </Container>
  );
};

export default Branch;
